import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import CostCenterSelector from 'components/Selects/OnboardingSelect';
import SimpleAlert from 'components/SimpleAlert';
import TextArea from 'components/TextArea';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { CostCenter } from 'models/costCenter';
import { ProductType } from 'models/enums/productType';
import { BreakRulesReason, getBreakRulesReasonForTravel } from 'services/travelBreakRulesReasonService';
import { getReasonsForTravel, Reason } from 'services/travelReasonService';
import { getFormattedPrice } from 'utils/currency';

import ReasonSelect from './components/ReasonSelect';
import { mapCostCenterToReason } from './mapper';
import {
  Container,
  Title,
  ItineraryCard,
  AirplaneIcon,
  ItineraryText,
  Value,
  ButtonContainer,
  StyledField,
  StyledButton,
  CarIcon,
  HotelIcon,
  NoShowText,
  NoShowTextImportant,
  AlternativeValue,
  ItineraryCardAlternative,
  AddCardBtn,
  PayAtDestination,
  PayAtDestinationText,
  PayAtDestinationContainer,
  PayAtDestinationIcon,
  PayAtDestinationTitle,
} from './styles';
import { getAllPaymentCardsByUserId } from 'services/paymentMethodService';
import { PaymentCard, PaymentMethod } from 'models/paymentCard';
import CardSelect from './components/CardsSelect';
import useUserAccount from 'hooks/useUserAccount';
import PaymentAddCardContainer from '../Payment/PaymentAddCard';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';
import { PaymentExceptionProduct, PaymentExceptionResponse } from 'models/paymentException';
import { getAllPaymentExceptions } from 'services/paymentExceptionService';

interface Props {
  show: boolean;
  itineraryValue?: number;
  currencyCode?: string;
  loading: boolean;
  isValid: boolean;
  onClose: () => void;
  onSubmit: () => void;
  productType: ProductType;
  costCenters?: CostCenter[];
  breakRulesMessage?: string | string[];
  alternativePrice?: number;
  alternativePriceCurrencyCode?: string;
  travelerId?: string;
  onAdded?: (cardId?: string) => void;
  havePayAtDestination?: boolean;
  paymentExceptionProvider?: string;
  paymentExceptionAirlines?: string[];
  onCreditCardByUserChange?: (creditCardByUser: boolean) => void;
}

const TravelJustify = ({
  show,
  onClose,
  isValid,
  loading,
  currencyCode,
  itineraryValue,
  onSubmit,
  productType,
  costCenters,
  breakRulesMessage,
  alternativePrice,
  alternativePriceCurrencyCode,
  travelerId,
  onAdded,
  havePayAtDestination,
  paymentExceptionProvider,
  paymentExceptionAirlines,
  onCreditCardByUserChange,
}: Props) => {
  const { t } = useStoreTranslation();
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [breakRulesReasons, setBreakRulesReasons] = useState<BreakRulesReason[]>([]);
  const reasonSelectRef = useRef(null);
  const cardSelectRef = useRef(null);
  const costCenterSelectRef = useRef(null);
  const breakRulesReasonSelectRef = useRef(null);
  const userAccount = useUserAccount();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | undefined>();
  const [showCard, setShowCard] = useState(false);
  const companyConfiguration = useCompanyConfiguration();
  const [hasCreditCardByUserException, setHasCreditCardByUserException] = useState(false);
  const paymentExceptionAirlineCodes = useMemo(() => paymentExceptionAirlines || [], [paymentExceptionAirlines]);

  const getPaymentExceptionProduct = useCallback((): PaymentExceptionProduct | undefined => {
    if (productType === ProductType.Flight) return 'air';
    if (productType === ProductType.Hotel) return 'hotel';
    if (productType === ProductType.Vehicle) return 'vehicle';
    return undefined;
  }, [productType]);

  const equalsIgnoringCase = useCallback(
    (value?: string | null, otherValue?: string | null) =>
      (value || '').toLowerCase() === (otherValue || '').toLowerCase(),
    [],
  );

  const hasAirlineException = useCallback((exception: PaymentExceptionResponse) => {
    if (productType !== ProductType.Flight) return true;
    if (!exception.airlines || exception.airlines.length === 0) return true;

    return paymentExceptionAirlineCodes.some((airline) =>
      exception.airlines?.some((exceptionAirline) => equalsIgnoringCase(exceptionAirline, airline)),
    );
  }, [equalsIgnoringCase, paymentExceptionAirlineCodes, productType]);

  const hasProviderException = useCallback(
    (provider?: string | null) => {
      if (!provider) return true;

      return provider
        .split(',')
        .map((item) => item.trim())
        .some((item) => equalsIgnoringCase(item, paymentExceptionProvider));
    },
    [equalsIgnoringCase, paymentExceptionProvider],
  );

  const getPaymentException = useCallback(
    (paymentExceptions: PaymentExceptionResponse[]) => {
      const product = getPaymentExceptionProduct();
      if (!product || !paymentExceptions || paymentExceptions.length === 0) return undefined;

      return paymentExceptions.find(
        (paymentException) =>
          equalsIgnoringCase(paymentException.product, product) &&
          hasProviderException(paymentException.provider) &&
          hasAirlineException(paymentException),
      );
    },
    [equalsIgnoringCase, getPaymentExceptionProduct, hasAirlineException, hasProviderException],
  );

  useEffect(() => {
    if (!companyConfiguration?.lineOfCreditAvailable || !userAccount.companyId) {
      setHasCreditCardByUserException(false);
      return;
    }

    getAllPaymentExceptions(userAccount.companyId)
      .then((paymentExceptions) => {
        const paymentException = getPaymentException(paymentExceptions);
        setHasCreditCardByUserException(Boolean(paymentException?.creditCardByUser));
      })
      .catch(() => setHasCreditCardByUserException(false));
  }, [companyConfiguration?.lineOfCreditAvailable, userAccount.companyId, getPaymentException]);

  const creditCardByUser = userAccount.creditCardByUser || hasCreditCardByUserException;

  useEffect(() => {
    if (onCreditCardByUserChange) onCreditCardByUserChange(Boolean(creditCardByUser));
  }, [creditCardByUser, onCreditCardByUserChange]);

  useEffect(() => {
    getReasonsForTravel().then((res) => {
      setReasons(res.data);
    });
    getPaymentCards();
    if (breakRulesMessage) {
      getBreakRulesReasonForTravel().then((res) => {
        setBreakRulesReasons(res.data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedValue = useMemo(() => {
    const formattedPrice = (value: number | undefined) => getFormattedPrice({ value, currency: currencyCode, t });
    if (itineraryValue) {
      return formattedPrice(itineraryValue);
    }
    return undefined;
  }, [t, currencyCode, itineraryValue]);

  const formattedAlternativeValue = useMemo(() => {
    const formattedPrice = (value: number | undefined, currencyCode: string) =>
      getFormattedPrice({ value, currency: alternativePriceCurrencyCode, t });
    if (alternativePrice) {
      return formattedPrice(alternativePrice, alternativePriceCurrencyCode);
    }
    return undefined;
  }, [alternativePrice, alternativePriceCurrencyCode, t]);

  const handleSelectReason = (reason: Reason) => (reason ? reason.name : '');
  const handleSelectCard = (card: PaymentCard) => (card ? card.cardId : '');
  const handleSelectCostCenter = (costCenter: any) => (costCenter ? costCenter.name : '');
  const handleSelectBreakRulesReason = (reason: BreakRulesReason) => (reason ? reason.name : '');


  const isBreakRulesMessageValid = () => {
    if (breakRulesMessage && breakRulesMessage.length > 0 && breakRulesReasons && breakRulesReasons.length > 0) {
      if ((document.getElementById('custom_select_breakRulesReason') as HTMLInputElement)?.value === '') {
        return false;
      } else {
        return true;
      }
    }

    return true;
  };

  const getPaymentCards = () => {
    // userid
    getAllPaymentCardsByUserId(travelerId).then((res) => {
      const cards = res.map((item)=> {
        item.card.name = `${translateMessage('end')} ${item.card.last4Digits}`
        return item.card
      });
      
      setCards(cards);
    })
  }
  const translateMessage = useCallback(
    (msg) => {
      const text = `payment.${msg}`;
      return t(text) !== text ? t(text) : msg;
    },
    [t],
  );
  const renderAddCard = () => (
    <ApplicationOverlayContent sidebar show={showCard} onClose={() => setShowCard(false)}>
      <PaymentAddCardContainer
        selectedMethod={selectedMethod}
        userId={travelerId}
        onCloseForm={() => setShowCard(false)}
        onAdded={(cardId?: string) => {
          setShowCard(false);
          if (onAdded) onAdded(cardId);
          getPaymentCards();
        }}
      />
    </ApplicationOverlayContent>
  );
  return (
    <ApplicationOverlayContent show={show} onClose={onClose} sidebar>
      <Desktop>
        <ModalHeader title={t('travelJustify.header')} />
      </Desktop>
      <Mobile>
        <AppBar goBack={onClose}>
          <h5>{t('travelJustify.header')}</h5>
        </AppBar>
      </Mobile>
      <Container>
        <Title>{t('travelJustify.title')}</Title>
        <StyledField name="reason">
          <CustomSelect
            name="reason"
            label={t('travelJustify.reasonSelectLabel')}
            placeHolder={t('travelJustify.reasonSelectLabel')}
            selectRef={reasonSelectRef}
            onChange={handleSelectReason}
            testId="travel-justify-reason"
          >
            {({ ...selectProps }: CustomSelectRenderProps) => (
              <ReasonSelect {...selectProps} selectRef={reasonSelectRef} reasons={reasons} />
            )}
          </CustomSelect>
        </StyledField>
        {creditCardByUser && (
          <StyledField name="card">
          <CustomSelect
            name="card"
            label={t('travelJustify.cardSelectLabel')}
            placeHolder={t('travelJustify.cardSelectLabel')}
            selectRef={cardSelectRef}
            onChange={handleSelectCard}
            testId="travel-justify-card"
          >
            {({ ...selectProps }: CustomSelectRenderProps) => (
              <CardSelect {...selectProps} selectRef={cardSelectRef} cards={cards} />
            )}
          </CustomSelect>
        </StyledField>
        )}
        {renderAddCard()}
        {creditCardByUser && (
                  <AddCardBtn onClick={() => {
                    setShowCard(true);
                  }}>+ {t('payment.cardList.btnAddCard')}</AddCardBtn>
        )}

        {!companyConfiguration?.menuView?.hideTravelReason && (
            <StyledField  name="justifyForBreakingRules" label={t('travelJustify.justifyLabel')}>
              <TextArea data-testid="travel-justify-textarea" />
            </StyledField>
        )}
        
        {costCenters && costCenters.length > 0 && (
          <StyledField name="costCenter" label={t('travelJustify.costCenterLabel')}>
            <CostCenterSelector
              type="costCenter"
              name="costCenter"
              options={mapCostCenterToReason(costCenters)}
              title={t('travelJustify.costCenterLabel')}
              enableSearch={true}
              placeholder={t('travelJustify.costCenterLabel')}
              selectRef={costCenterSelectRef}
              onChange={handleSelectCostCenter}
            />
          </StyledField>
        )}

        {breakRulesMessage && breakRulesMessage.length > 0 && breakRulesReasons && breakRulesReasons.length > 0 && (
          <>
            <br />
            {breakRulesMessage.length > 0 && typeof breakRulesMessage !== 'string' && (
              <SimpleAlert
                title={t('travelJustify.breakRulesReason.outOfPolicies')}
                content={
                  <ul>
                    {(breakRulesMessage as string[])?.map((msg) => (
                      <li key={msg}>&bull; {msg}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {typeof breakRulesMessage === 'string' && (
              <SimpleAlert
                title={t('travelJustify.breakRulesReason.outOfPolicies')}
                content={
                  <ul>
                    <li key="breakRulesMessage1">&bull; {breakRulesMessage}</li>
                  </ul>
                }
              />
            )}
            <StyledField name="breakRulesReason" label={t('travelJustify.breakRulesReason.selectLabel')}>
              <CustomSelect
                name="breakRulesReason"
                placeHolder={t('travelJustify.breakRulesReason.selectLabel')}
                selectRef={breakRulesReasonSelectRef}
                onChange={handleSelectBreakRulesReason}
                testId="travel-justify-breakreason"
              >
                {({ ...selectProps }: CustomSelectRenderProps) => (
                  <ReasonSelect {...selectProps} selectRef={breakRulesReasonSelectRef} reasons={breakRulesReasons} />
                )}
              </CustomSelect>
            </StyledField>
          </>
        )}

        <ItineraryCard $hasAlternativePrice={alternativePrice !== undefined}>
          {productType === ProductType.Vehicle && <CarIcon />}
          {productType === ProductType.Flight && (
            <AirplaneIcon src="/corporate/images/booking-checkout-airplane.svg" alt="" />
          )}
          {productType === ProductType.Hotel && <HotelIcon />}

          <ItineraryText>{t('travelJustify.itinerary')}</ItineraryText>
          <Value>
            <b>{formattedValue?.formattedWithoutCode}</b>
            {formattedValue?.currency}
          </Value>
        </ItineraryCard>

        {alternativePrice && alternativePriceCurrencyCode && (
          <ItineraryCardAlternative>
            <AlternativeValue>
              {t('alternativePrice.or')}&nbsp;
              <b>{formattedAlternativeValue?.formattedWithoutCode}</b>
              {formattedAlternativeValue?.currency}
            </AlternativeValue>
          </ItineraryCardAlternative>
        )}

        {productType === ProductType.Hotel && (
          <>
          {havePayAtDestination && (
            <PayAtDestination>
              <PayAtDestinationContainer>
                <PayAtDestinationTitle><PayAtDestinationIcon />{t('travelJustify.payAtDestinationTitle')}</PayAtDestinationTitle>
              <PayAtDestinationText>{t('travelJustify.payAtDestinationText')}</PayAtDestinationText>
              </PayAtDestinationContainer>
             
            </PayAtDestination>
          )}
            <NoShowTextImportant>{t('travelJustify.noShowTextImportant')}</NoShowTextImportant>
            <NoShowText>
              {companyConfiguration?.email
                ? t('travelJustify.noShowText', { email: companyConfiguration.email })
                : t('travelJustify.noShowTextWithoutEmail')}
            </NoShowText>
          </>
        )}
        <ButtonContainer>
          <StyledButton
            type="submit"
            onClick={onSubmit}
            value={t('travelJustify.submitText')}
            disabled={!isValid || loading || !isBreakRulesMessageValid()}
            loading={loading}
            testId="travel-justify-submit-button"
          />
        </ButtonContainer>
      </Container>
    </ApplicationOverlayContent>
  );
};

export default TravelJustify;
